import React, { useMemo, useRef, useState } from 'react';
import {
    SectionContentWrapper,
    SectionDashRoot,
    SectionDescription,
    SectionTitle,
    SubsectionTitle,
    SubsectionWrapper,
} from '../../components/common.styles';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
    FormBuilderLayout,
    FormPreviewWrapper,
    FormSettingsWrapper,
    PlatformsFormWrapper,
    QrWrapper,
} from './collect-section.styles';
import { ReviewForm } from '../../review-form/review-form.component';
import { UserPopulatedType } from '../../types/user-data.type';
import { ReviewSource } from '../../types/review-source.enum';
import { ReactComponent as GLogo } from '../../assets/google.svg';
import { ReactComponent as YLogo } from '../../assets/yelp.svg';
import { ReactComponent as FLogo } from '../../assets/facebook.svg';
import { ReactComponent as TLogo } from '../../assets/trustpilot.svg';
import axios from 'axios';
import { defaultAccentColor, SERVER_URL } from '../../constants';
import { LogoUploadComponent } from './logo-upload.component';
import { FormPlatformCheckboxComponent } from './form-platform-checkbox.component';
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { getAvailableUserPlatforms } from '../../utils/getAvailableUserPlatforms';
import QRCode from 'qrcode.react';
import { ReactComponent as ImageIcon } from '../../assets/image.svg';
import { ReviewFormRoot, ReviewFormTitle } from '../../review-form/review-form.styles';
import { LockIcon } from '../../components/lock-icon.component';
import { PaidFeatureModal } from '../../components/paid-feature-modal/paid-feature.modal';

interface Props {
    userPopulatedData: UserPopulatedType;
    onUpgrade: () => void;
    isProPlan: boolean;
}
export const ReviewFormBuilder = ({ userPopulatedData, onUpgrade, isProPlan }: Props) => {
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(userPopulatedData.user.logo);
    const qrCodeRef = useRef<HTMLDivElement>(null);
    const [formReload, setFormReload] = useState(0);
    const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
    const [showPaidFeatureModal, setShowPaidFeatureModal] = useState(false);

    const availableBoostPlatforms = useMemo(() => getAvailableUserPlatforms(userPopulatedData), [userPopulatedData.user]);
    const [selectedBoostPlatforms, setSelectedBoostPlatforms] = useState<ReviewSource[]>(
        userPopulatedData.user.settings.boostPlatforms?.length
            ? userPopulatedData.user.settings.boostPlatforms
            : getAvailableUserPlatforms(userPopulatedData)
    );

    const onBoostPlatformChange = async (value: ReviewSource[]) => {
        setSelectedBoostPlatforms(value);
        const token = localStorage.getItem('access_token');
        if (token) {
            await axios.patch(
                `${SERVER_URL}/user`,
                {
                    email: userPopulatedData.user.email,
                    settings: {
                        ...userPopulatedData.user.settings,
                        boostPlatforms: value,
                    },
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        }
    };

    const downloadQRCode = () => {
        const canvas = qrCodeRef.current?.querySelector('canvas');
        console.log(canvas);
        if (canvas) {
            const url = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = url;
            link.download = 'qr-code.png';
            link.click();
        }
    };

    const surveyUrl = `https://survey.hybrid-reach.com/?id=${userPopulatedData.user._id}`;

    return (
        <SectionDashRoot>
            <SectionContentWrapper $fullHeight>
                <FormBuilderLayout>
                    <FormSettingsWrapper>
                        <SectionTitle>Reputation Builder</SectionTitle>
                        <SectionDescription>
                            Boost the number of great reviews by sharing the request review form with clients, that filters
                            low-rated feedback
                        </SectionDescription>
                        <Stack gap={3} sx={{ mt: 6 }}>
                            <LogoUploadComponent value={userPopulatedData.user.logo} onLogoUrlChange={setUploadedLogoUrl} />
                            <SubsectionWrapper>
                                <SubsectionTitle $margin>Select available platforms</SubsectionTitle>
                                <FormGroup
                                    onChange={(e) => {
                                        const source = (e.target as HTMLInputElement).name as ReviewSource;
                                        if (selectedBoostPlatforms.includes(source)) {
                                            onBoostPlatformChange(selectedBoostPlatforms.filter((s) => s !== source));
                                        } else {
                                            onBoostPlatformChange([...selectedBoostPlatforms, source]);
                                        }
                                    }}
                                >
                                    <PlatformsFormWrapper>
                                        <FormPlatformCheckboxComponent
                                            checked={selectedBoostPlatforms.includes(ReviewSource.google)}
                                            disabled={!availableBoostPlatforms.includes(ReviewSource.google)}
                                            logo={<GLogo />}
                                            name={ReviewSource.google}
                                            label={'Google'}
                                        />
                                        <FormPlatformCheckboxComponent
                                            checked={selectedBoostPlatforms.includes(ReviewSource.yelp)}
                                            disabled={!availableBoostPlatforms.includes(ReviewSource.yelp)}
                                            logo={<YLogo />}
                                            name={ReviewSource.yelp}
                                            label={'Yelp'}
                                        />
                                        <FormPlatformCheckboxComponent
                                            checked={selectedBoostPlatforms.includes(ReviewSource.facebook)}
                                            disabled={!availableBoostPlatforms.includes(ReviewSource.facebook)}
                                            logo={<FLogo />}
                                            name={ReviewSource.facebook}
                                            label={'Facebook'}
                                        />
                                        <FormPlatformCheckboxComponent
                                            checked={selectedBoostPlatforms.includes(ReviewSource.trustpilot)}
                                            disabled={!availableBoostPlatforms.includes(ReviewSource.trustpilot)}
                                            logo={<TLogo />}
                                            name={ReviewSource.trustpilot}
                                            label={'Trustpilot'}
                                        />
                                    </PlatformsFormWrapper>
                                </FormGroup>
                            </SubsectionWrapper>
                            <SubsectionWrapper>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                                    <SubsectionTitle>Link to survey form</SubsectionTitle>
                                    <Tooltip
                                        placement="top"
                                        disableHoverListener={isProPlan}
                                        title={
                                            <Typography fontSize="14px" fontWeight="500">
                                                PRO Feature
                                            </Typography>
                                        }
                                    >
                                        <Tooltip placement="top" title="Copied to the clipboard" open={copyTooltipOpen}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                gap={1}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    if (!isProPlan) {
                                                        setShowPaidFeatureModal(true);
                                                    }
                                                }}
                                            >
                                                <Button
                                                    disabled={!isProPlan}
                                                    variant="contained"
                                                    startIcon={<CopyIcon />}
                                                    sx={{
                                                        '& .MuiButton-startIcon path': {
                                                            fill: !isProPlan ? '#CDCED6' : '#fff',
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(surveyUrl);
                                                        setCopyTooltipOpen(true);
                                                        setTimeout(() => {
                                                            setCopyTooltipOpen(false);
                                                        }, 2000);
                                                    }}
                                                >
                                                    Copy
                                                </Button>
                                                {!isProPlan && <LockIcon size={20} />}
                                            </Stack>
                                        </Tooltip>
                                    </Tooltip>
                                </Stack>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={isProPlan ? surveyUrl : surveyUrl.slice(0, 32)}
                                    disabled={!isProPlan}
                                    sx={{
                                        opacity: !isProPlan ? 0.6 : 1,
                                        '& .MuiInputBase-input': {
                                            textOverflow: 'ellipsis',
                                        },
                                    }}
                                />
                            </SubsectionWrapper>
                            <SubsectionWrapper>
                                <Stack gap={3} alignItems="center">
                                    <SubsectionTitle>QR Code</SubsectionTitle>
                                    <QrWrapper ref={qrCodeRef}>
                                        <QRCode
                                            value={isProPlan && surveyUrl}
                                            size={500}
                                            style={{ filter: !isProPlan ? 'blur(4px)' : '' }}
                                        />
                                    </QrWrapper>
                                    <Tooltip
                                        placement="top"
                                        disableHoverListener={isProPlan}
                                        title={
                                            <Typography fontSize="14px" fontWeight="500">
                                                PRO Feature
                                            </Typography>
                                        }
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                if (!isProPlan) {
                                                    setShowPaidFeatureModal(true);
                                                }
                                            }}
                                        >
                                            <Button
                                                disabled={!isProPlan}
                                                variant={isProPlan ? 'outlined' : 'contained'}
                                                startIcon={<ImageIcon />}
                                                sx={{
                                                    '& .MuiButton-startIcon path': {
                                                        fill: !isProPlan ? '#CDCED6' : '#06070D',
                                                    },
                                                }}
                                                onClick={downloadQRCode}
                                            >
                                                Download
                                            </Button>
                                            {!isProPlan && <LockIcon size={20} />}
                                        </Stack>
                                    </Tooltip>
                                </Stack>
                            </SubsectionWrapper>
                        </Stack>
                    </FormSettingsWrapper>
                    <FormPreviewWrapper>
                        <ReviewFormRoot>
                            <ReviewFormTitle>Form preview</ReviewFormTitle>
                            <Button
                                variant="outlined"
                                color="info"
                                startIcon={<RefreshIcon />}
                                onClick={() => {
                                    setFormReload(formReload + 1);
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                }}
                                size="small"
                            >
                                Refresh
                            </Button>
                            <ReviewForm
                                userId={userPopulatedData.user._id}
                                email={userPopulatedData.user.email}
                                companyName={userPopulatedData.user.companyName}
                                selectedBoostPlatforms={selectedBoostPlatforms}
                                googleCompanyId={userPopulatedData.user.googleCompanyId}
                                yelpAlias={userPopulatedData.user.yelpAlias}
                                facebookAlias={userPopulatedData.user.facebookAlias}
                                trustpilotAlias={userPopulatedData.user.trustpilotAlias}
                                logoUrl={uploadedLogoUrl}
                                reload={formReload}
                                accentColor={userPopulatedData.user.settings.accentColor || defaultAccentColor}
                            />
                        </ReviewFormRoot>
                    </FormPreviewWrapper>
                </FormBuilderLayout>
            </SectionContentWrapper>
            <PaidFeatureModal open={showPaidFeatureModal} onClose={() => setShowPaidFeatureModal(false)} onUpgrade={onUpgrade} />
        </SectionDashRoot>
    );
};

const RefreshIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="Update">
            <path
                id="Vector"
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.96216 8.38278C4.2459 7.3229 4.80365 6.35638 5.57935 5.5804C6.35506 4.80442 7.32137 4.24632 8.38115 3.9622C9.44093 3.67808 10.5568 3.67796 11.6167 3.96185C12.6765 4.24574 13.643 4.80363 14.4188 5.57945L16.0047 7.16528H13.3522C13.1864 7.16528 13.0274 7.23113 12.9102 7.34834C12.793 7.46555 12.7272 7.62452 12.7272 7.79028C12.7272 7.95604 12.793 8.11501 12.9102 8.23222C13.0274 8.34943 13.1864 8.41528 13.3522 8.41528H17.5122C17.6779 8.41528 17.8369 8.34943 17.9541 8.23222C18.0713 8.11501 18.1372 7.95604 18.1372 7.79028V3.63028C18.1372 3.46452 18.0713 3.30555 17.9541 3.18834C17.8369 3.07113 17.6779 3.00528 17.5122 3.00528C17.3464 3.00528 17.1874 3.07113 17.0702 3.18834C16.953 3.30555 16.8872 3.46452 16.8872 3.63028V6.28028L15.3038 4.69695C14.373 3.76566 13.2133 3.09585 11.9415 2.75487C10.6697 2.41388 9.33052 2.41374 8.05863 2.75445C6.78674 3.09517 5.62696 3.76473 4.6959 4.69582C3.76485 5.62691 3.09533 6.78671 2.75466 8.05861C2.73343 8.13796 2.72804 8.2207 2.73878 8.30212C2.74953 8.38355 2.77621 8.46206 2.8173 8.53318C2.85839 8.60429 2.91309 8.66661 2.97826 8.71659C3.04344 8.76657 3.11782 8.80322 3.19716 8.82445C3.2765 8.84568 3.35925 8.85107 3.44067 8.84033C3.5221 8.82958 3.60061 8.8029 3.67172 8.76181C3.74284 8.72072 3.80516 8.66602 3.85514 8.60085C3.90511 8.53567 3.94176 8.46129 3.96299 8.38195L3.96216 8.38278ZM16.8022 11.1761C16.7229 11.1548 16.6402 11.1493 16.5588 11.16C16.4774 11.1707 16.3989 11.1973 16.3277 11.2383C16.2566 11.2793 16.1943 11.3339 16.1443 11.399C16.0943 11.4642 16.0576 11.5385 16.0363 11.6178C15.7526 12.6777 15.1948 13.6442 14.4191 14.4202C13.6434 15.1961 12.6771 15.7542 11.6173 16.0384C10.5576 16.3225 9.44165 16.3226 8.38181 16.0387C7.32196 15.7548 6.35553 15.1969 5.57966 14.4211L3.99466 12.8353H6.64716C6.81292 12.8353 6.97189 12.7694 7.0891 12.6522C7.20631 12.535 7.27216 12.376 7.27216 12.2103C7.27216 12.0445 7.20631 11.8855 7.0891 11.7683C6.97189 11.6511 6.81292 11.5853 6.64716 11.5853H2.48633C2.32057 11.5853 2.1616 11.6511 2.04439 11.7683C1.92718 11.8855 1.86133 12.0445 1.86133 12.2103V16.3703C1.86133 16.536 1.92718 16.695 2.04439 16.8122C2.1616 16.9294 2.32057 16.9953 2.48633 16.9953C2.65209 16.9953 2.81106 16.9294 2.92827 16.8122C3.04548 16.695 3.11133 16.536 3.11133 16.3703V13.7203L4.69466 15.3036C5.62558 16.2349 6.78527 16.9047 8.05714 17.2456C9.32901 17.5865 10.6682 17.5866 11.9401 17.2458C13.212 16.905 14.3718 16.2354 15.3028 15.3042C16.2338 14.373 16.9033 13.2131 17.2438 11.9411C17.2866 11.7811 17.2641 11.6107 17.1813 11.4672C17.0985 11.3238 16.9621 11.2191 16.8022 11.1761Z"
                fill="#06070D"
            />
        </g>
    </svg>
);
